export const FONT_FAMILY_ROLES = ['display', 'heading', 'body'] as const;
export type FontFamilyRole = (typeof FONT_FAMILY_ROLES)[number];

export const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
export type HeadingLevel = (typeof HEADING_LEVELS)[number];

/** Weights the Admin typography inventory writes when the operator has not chosen one. */
export const ADMIN_DEFAULT_FONT_WEIGHTS = {
  primary: '900',
  secondary: '400',
  monospace: '400',
} as const;

const ROLE_ALIASES: Readonly<Record<string, readonly FontFamilyRole[]>> = {
  primary: ['heading', 'display'],
  secondary: ['body'],
};

export interface TypographyStateEntry {
  fontFamily?: string | null;
  fontWeight?: string | null;
}

export interface ResolveThemeFontTokensInput {
  fontFamily?: Record<string, string | null | undefined> | null;
  typographyState?: Record<
    string,
    TypographyStateEntry | null | undefined
  > | null;
  cssVariables?: Record<string, string | null | undefined> | null;
  headingLevels?: Partial<
    Record<HeadingLevel, FontFamilyRole | null | undefined>
  > | null;
}

export interface ResolvedThemeFontTokens {
  variables: Record<string, string>;
}

function nonEmpty(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isFontFamilyRole(value: string): value is FontFamilyRole {
  return (FONT_FAMILY_ROLES as readonly string[]).includes(value);
}

function toKebabCase(value: string): string {
  if (value === 'DEFAULT') return 'default';
  return value.replace(/([A-Z])/g, '-$1').toLowerCase();
}

function isHeadingLevel(value: string): value is HeadingLevel {
  return (HEADING_LEVELS as readonly string[]).includes(value);
}

function aliasFamily(
  role: FontFamilyRole,
  state: Record<string, TypographyStateEntry | null | undefined>,
  explicitRoles: ReadonlySet<string>,
): string | undefined {
  if (explicitRoles.has(role)) return undefined;
  for (const [stateName, roles] of Object.entries(ROLE_ALIASES)) {
    if (!roles.includes(role)) continue;
    const family = nonEmpty(state[stateName]?.fontFamily);
    if (family) return family;
  }
  return undefined;
}

/**
 * Resolves Theme v2 font families and explicit role weights to CSS custom properties.
 *
 * Family precedence for display, heading, and body:
 * `font.family` > a typography state named that role > the primary/secondary alias >
 * `cssVariables`.
 */
export function resolveThemeFontTokens(
  input: ResolveThemeFontTokensInput,
): ResolvedThemeFontTokens {
  const state = input.typographyState ?? {};
  const explicitRoles = new Set(
    Object.entries(state).flatMap(([name, value]) =>
      isFontFamilyRole(name) && nonEmpty(value?.fontFamily) ? [name] : [],
    ),
  );

  const families: Partial<Record<FontFamilyRole, string>> = {};
  const variables: Record<string, string> = {};

  for (const role of FONT_FAMILY_ROLES) {
    const resolved =
      nonEmpty(input.fontFamily?.[role]) ??
      (explicitRoles.has(role)
        ? nonEmpty(state[role]?.fontFamily)
        : undefined) ??
      aliasFamily(role, state, explicitRoles) ??
      nonEmpty(input.cssVariables?.[`--font-family-${role}`]);
    if (!resolved) continue;
    families[role] = resolved;
    variables[`--font-family-${role}`] = resolved;
  }

  for (const [name, value] of Object.entries(state)) {
    if (isFontFamilyRole(name)) continue;
    const family = nonEmpty(value?.fontFamily);
    if (family) variables[`--font-family-${toKebabCase(name)}`] = family;
  }

  for (const level of HEADING_LEVELS) {
    const role = input.headingLevels?.[level];
    if (role !== 'display' && role !== 'heading' && role !== 'body') continue;
    const family = families[role];
    if (family) variables[`--font-family-${level}`] = family;
  }

  const primaryWeight = nonEmpty(state.primary?.fontWeight);
  if (primaryWeight && primaryWeight !== ADMIN_DEFAULT_FONT_WEIGHTS.primary) {
    variables['--font-weight-display'] = primaryWeight;
    variables['--font-weight-heading'] = primaryWeight;
  }

  const secondaryWeight = nonEmpty(state.secondary?.fontWeight);
  if (
    secondaryWeight &&
    secondaryWeight !== ADMIN_DEFAULT_FONT_WEIGHTS.secondary
  ) {
    variables['--font-weight-body'] = secondaryWeight;
  }

  return { variables };
}

export const FONT_FAMILY_PARITY_FIXTURE: ResolveThemeFontTokensInput = {
  fontFamily: {
    display: 'DM Sans',
    heading: 'DM Sans',
    body: 'Apercu Pro',
  },
  typographyState: {
    primary: { fontFamily: 'Helvetica Neue', fontWeight: '900' },
    secondary: { fontFamily: 'Inter', fontWeight: '400' },
    monospace: { fontFamily: 'IBM Plex Mono', fontWeight: '400' },
    brandDisplay: { fontFamily: 'Recoleta' },
  },
  cssVariables: {
    '--font-family-heading': 'Comic Sans MS',
    '--font-family-body': 'Papyrus',
    '--color-note': '#111111',
  },
  headingLevels: { h5: 'body' },
};

export const FONT_FAMILY_PARITY_EXPECTED: Record<string, string> = {
  '--font-family-display': 'DM Sans',
  '--font-family-heading': 'DM Sans',
  '--font-family-body': 'Apercu Pro',
  '--font-family-h5': 'Apercu Pro',
  '--font-family-primary': 'Helvetica Neue',
  '--font-family-secondary': 'Inter',
  '--font-family-monospace': 'IBM Plex Mono',
  '--font-family-brand-display': 'Recoleta',
};

export const FONT_FAMILY_PARITY_ABSENT = [
  '--font-weight-display',
  '--font-weight-heading',
  '--font-weight-body',
  '--font-family-brandDisplay',
  'Comic Sans MS',
  'Papyrus',
] as const;

export function fontFamilyParityGaps(css: string): string[] {
  const gaps: string[] = [];
  for (const [key, value] of Object.entries(FONT_FAMILY_PARITY_EXPECTED)) {
    if (!css.includes(`${key}: ${value};`)) gaps.push(`missing ${key}`);
  }
  for (const token of FONT_FAMILY_PARITY_ABSENT) {
    if (css.includes(token)) gaps.push(`unexpected ${token}`);
  }
  return gaps;
}
